import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { ZODIAC_EXTRAS, zodiacSlug } from "@/lib/calendar/zodiac";
import { computePair, pairSlug, parsePairSlug } from "@/lib/calendar/zodiac-compat";
import { withSlash } from "@/lib/url";

export const Route = createFileRoute("/zodiak/cocok/$pair")({
  loader: ({ params }) => {
    const parsed = parsePairSlug(params.pair);
    if (!parsed) throw notFound();
    const canonical = pairSlug(parsed.a, parsed.b);
    if (canonical !== params.pair.toLowerCase()) {
      throw redirect({ to: `/zodiak/cocok/${canonical}/` } as any);
    }
    return parsed;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { a, b } = loaderData;
    const score = computePair(a, b);
    const title = `Kecocokan ${a.nameId} dan ${b.nameId} - Skor ${score.overall}%`;
    const desc = `Kecocokan zodiak ${a.nameId} & ${b.nameId}: cinta ${score.love}%, sahabat ${score.friend}%, kerja ${score.work}%. ${score.summaryId}`;
    const canonical = `https://hari.co.id/zodiak/cocok/${pairSlug(a, b)}/`;
    const requested = `https://hari.co.id${withSlash(`/zodiak/cocok/${params.pair}`)}`;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Beranda", item: "https://hari.co.id/" },
          { "@type": "ListItem", position: 2, name: "Zodiak", item: "https://hari.co.id/zodiak/" },
          { "@type": "ListItem", position: 3, name: "Kecocokan", item: "https://hari.co.id/zodiak/cocok/" },
          { "@type": "ListItem", position: 4, name: `${a.nameId} & ${b.nameId}`, item: canonical },
        ],
      },
    ];
    return {
      meta: [
        { title }, { name: "description", content: desc },
        { property: "og:title", content: title }, { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }, ...(canonical !== requested ? [] : [])],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(ld) }],
    };
  },
  component: PairPage,
  notFoundComponent: () => (
    <SiteShell><div className="py-16 text-center"><h1 className="text-2xl font-bold">Pasangan zodiak tidak ditemukan</h1>
      <Link to={withSlash("/zodiak/cocok") as never} className="mt-4 inline-block text-primary hover:underline">Kembali ke daftar kecocokan</Link>
    </div></SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (<SiteShell><div className="py-16 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Coba lagi</button>
    </div></SiteShell>);
  },
});

function PairPage() {
  const { a, b } = Route.useLoaderData();
  const s = computePair(a, b);
  const xa = ZODIAC_EXTRAS[a.id];
  const xb = ZODIAC_EXTRAS[b.id];
  return (
    <SiteShell>
      <div className="mx-auto max-w-3xl py-8">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link>{" / "}
          <Link to={withSlash("/zodiak") as never} className="hover:text-primary">Zodiak</Link>{" / "}
          <Link to={withSlash("/zodiak/cocok") as never} className="hover:text-primary">Kecocokan</Link>{" / "}
          {a.nameId} & {b.nameId}
        </nav>

        <header className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-5xl">{a.symbol}<span className="text-muted-foreground text-2xl">×</span>{b.symbol}</div>
          <div>
            <h1 className="font-display text-3xl tracking-tight md:text-4xl">{a.nameId} & {b.nameId}</h1>
            <p className="text-muted-foreground text-sm">Elemen {a.elementId} × {b.elementId} · Modalitas {xa.modality} × {xb.modality}</p>
          </div>
        </header>

        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          {[
            ["Skor Total", s.overall],
            ["Cinta", s.love],
            ["Sahabat", s.friend],
            ["Kerja", s.work],
          ].map(([label, val]) => (
            <div key={label as string} className="rounded-lg border border-border bg-card p-4 text-center">
              <div className="text-3xl font-bold text-primary">{val}%</div>
              <div className="mt-1 text-xs text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-base leading-relaxed">{s.summaryId}</p>

        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold text-green-700 dark:text-green-400">Kekuatan Pasangan</h2>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
              {s.strengths.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <h2 className="font-semibold text-amber-700 dark:text-amber-400">Yang Perlu Diperhatikan</h2>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
              {s.watchOuts.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
        </section>

        <section className="mt-6 rounded-lg border border-border bg-card p-5">
          <h2 className="font-semibold">Profil Singkat Kedua Zodiak</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 text-sm">
            <div>
              <Link to={withSlash(`/zodiak/${zodiacSlug(a)}`) as never} className="font-semibold text-primary hover:underline">{a.nameId} ({a.rangeId})</Link>
              <p className="mt-1 text-muted-foreground">{xa.shortBioId}</p>
            </div>
            <div>
              <Link to={withSlash(`/zodiak/${zodiacSlug(b)}`) as never} className="font-semibold text-primary hover:underline">{b.nameId} ({b.rangeId})</Link>
              <p className="mt-1 text-muted-foreground">{xb.shortBioId}</p>
            </div>
          </div>
        </section>

        <section className="mt-6">
          <h2 className="text-lg font-semibold">FAQ</h2>
          <div className="mt-3 space-y-3 text-sm">
            <div><div className="font-medium">Apakah {a.nameId} cocok dengan {b.nameId}?</div>
              <div className="text-muted-foreground">Skor total {s.overall}%. {s.summaryId}</div></div>
            <div><div className="font-medium">Bagaimana hubungan kerja {a.nameId} & {b.nameId}?</div>
              <div className="text-muted-foreground">Skor kerja {s.work}% — {xa.modality} & {xb.modality} {xa.modality === xb.modality ? "punya ritme serupa" : "saling mengisi dalam pengambilan keputusan"}.</div></div>
          </div>
        </section>

        <p className="mt-8 text-xs text-muted-foreground">
          Catatan: skor kompatibilitas adalah panduan referensi astrologi populer, bukan ramalan deterministik.
          Verifikasi dengan keyakinan dan pengalaman pribadi Anda.
        </p>
      </div>
    </SiteShell>
  );
}